'use client';
import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { Icon } from "@tameeni/icons";
import { Drawer } from "vaul";
import { useMediaQuery, cn } from "@tameeni/shared-logic";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ScrollArea } from "../scroll-area/scroll-area.js";
const Popup = ({
  children,
  open,
  isNotClosable,
  ...props
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  if (isDesktop) {
    return /* @__PURE__ */ jsx(DialogPrimitive.Root, { open: isNotClosable ? true : open, ...props, children });
  }
  return /* @__PURE__ */ jsx(
    Drawer.Root,
    {
      open: isNotClosable ? true : open,
      shouldScaleBackground: true,
      ...props,
      children
    }
  );
};
Popup.displayName = "Popup";
const PopupTrigger = DialogPrimitive.Trigger;
const PopupClose = DialogPrimitive.Close;
const PopupOverlay = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Overlay,
  {
    ref,
    className: cn(
      "fixed inset-0 z-50 bg-[rgba(164,172,183,0.40)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    ),
    ...props
  }
));
PopupOverlay.displayName = DialogPrimitive.Overlay.displayName;
const PopupContent = React.forwardRef(
  ({ className, children, isScrollable = true, overlayClassName, ...props }, ref) => {
    const isDesktop = useMediaQuery("(min-width: 768px)");
    if (isDesktop) {
      return /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { children: [
        /* @__PURE__ */ jsx(PopupOverlay, { className: overlayClassName }),
        /* @__PURE__ */ jsx(
          DialogPrimitive.Content,
          {
            ref,
            className: cn(
              "fixed left-[50%] h-fit max-h-[calc(100vh-4rem)] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-2xl",
              className
            ),
            ...props,
            children: isScrollable ? /* @__PURE__ */ jsx(ScrollArea, { className: "p-6 max-h-[inherit]", children }) : /* @__PURE__ */ jsx("div", { className: "p-6 h-full overflow-auto", children })
          }
        )
      ] });
    }
    return /* @__PURE__ */ jsxs(Drawer.Portal, { children: [
      /* @__PURE__ */ jsx(PopupOverlay, { className: overlayClassName }),
      /* @__PURE__ */ jsx(
        Drawer.Content,
        {
          ref,
          className: cn(
            "fixed inset-x-0 p-6  h-fit max-h-[calc(100vh-4rem)] bottom-0 z-50 mt-24 flex flex-col rounded-t-[10px] border bg-background w-full",
            className
          ),
          ...props,
          children: /* @__PURE__ */ jsx("div", { className: "max-h-[inherit] overflow-y-auto scrollbar-none", children })
        }
      )
    ] });
  }
);
PopupContent.displayName = DialogPrimitive.Content.displayName;
const PopupHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx("div", { className: cn("flex flex-col gap-4", className), ...props });
PopupHeader.displayName = "PopupHeader";
const PopupFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-10",
      className
    ),
    ...props
  }
);
PopupFooter.displayName = "PopupFooter";
const PopupTitle = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Title,
  {
    ref,
    className: cn("text-xl font-bold leading-7 text-slate-900", className),
    ...props
  }
));
PopupTitle.displayName = DialogPrimitive.Title.displayName;
const PopupDescription = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DialogPrimitive.Description,
  {
    ref,
    className: cn("text-sm font-bold leading-5 text-slate-600", className),
    ...props
  }
));
PopupDescription.displayName = DialogPrimitive.Description.displayName;
const PopupCloseX = ({
  notAbsolute = false,
  hasBackground = false
}) => /* @__PURE__ */ jsx(DialogPrimitive.Close, { className: "rounded-sm opacity-70 outline-none transition-opacity hover:opacity-100 disabled:pointer-events-none", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-row justify-between items-start ", children: [
  /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "flex-center end-4 top-4 rounded-full text-slate-600 p-[6px]",
        notAbsolute ? "static" : "absolute",
        hasBackground ? "bg-slate-50" : "bg-transparent"
      ),
      children: /* @__PURE__ */ jsx(Icon, { name: "Close", className: "w-5 h-5" })
    }
  ),
  /* @__PURE__ */ jsx("span", { className: "sr-only", children: "Close" })
] }) });
PopupCloseX.displayName = "PopupCloseX";
export {
  Popup,
  PopupClose,
  PopupCloseX,
  PopupContent,
  PopupDescription,
  PopupFooter,
  PopupHeader,
  PopupTitle,
  PopupTrigger
};
